import type { Cadeia } from '@core/tipos';
import type { EstadoLetra } from '@core/termo/avaliacao';
import { baixarArquivo } from './baixar';
import type { CelulaDesenho, Enquadramento, PaletaImagem } from './desenho';
import {
  caixaDe,
  celulasEmOrdem,
  enquadrar,
  pintarCelula,
  pintarFundo,
  pintarLegenda,
  venceOCruzamento,
} from './desenho';
import type { Cor, QuadroGif } from './gif';
import { codificarGif, indexar, paletaMediana } from './gif';

/**
 * O tabuleiro como GIF animado, digitado letra a letra (§7.5).
 *
 * A animação conta o que a imagem parada não conta: a ordem em que a cadeia
 * foi construída. Cada palavra aparece letra a letra, na ordem em que foi
 * jogada, e no fim o tabuleiro fica quieto por um tempo antes de recomeçar.
 *
 * Os quadros são **parciais**: cada um repinta só o retângulo das letras que
 * entraram, com descarte "deixar como está". É o que mantém o arquivo pequeno
 * — o fundo e tudo o que já foi digitado viajam uma vez só, no primeiro
 * quadro, que é o único do tamanho da tela.
 */

/** Quantos quadros de digitação, no máximo. */
const PASSOS_MAX = 40;

/** Duração de cada passo, em centésimos de segundo. */
const PASSO_CS = 9;

/** O tabuleiro pronto fica parado antes de o laço recomeçar. */
const PAUSA_FINAL_CS = 220;

/** Respiro antes de a primeira letra cair. */
const ABERTURA_CS = 40;

/** Cores da paleta. O tabuleiro é chapado; o que gasta é o texto suavizado. */
const CORES = 128;

export interface OpcoesGifTabuleiro {
  readonly cadeia: Cadeia;
  /** §23: marcas do dia por palavra. Ausente fora da Trama. */
  readonly marcas?: ReadonlyMap<string, readonly EstadoLetra[]> | undefined;
  readonly legenda: string;
  readonly paleta: PaletaImagem;
}

/** Divide as células em levas, para uma cadeia longa não virar mil quadros. */
function emLevas(celulas: readonly CelulaDesenho[]): readonly (readonly CelulaDesenho[])[] {
  if (celulas.length === 0) return [];
  const porPasso = Math.ceil(celulas.length / PASSOS_MAX);
  const levas: CelulaDesenho[][] = [];
  for (let i = 0; i < celulas.length; i += porPasso) {
    levas.push([...celulas.slice(i, i + porPasso)]);
  }
  return levas;
}

interface QuadroCru {
  readonly esquerda: number;
  readonly topo: number;
  readonly dados: ImageData;
  readonly atrasoCs: number;
}

/**
 * Monta o GIF e devolve o blob, ou `undefined` se o canvas não estiver
 * disponível — o mesmo caminho silencioso do PNG.
 */
export function gerarGifDoTabuleiro(opcoes: OpcoesGifTabuleiro): Blob | undefined {
  const { cadeia, marcas, paleta } = opcoes;

  const enq: Enquadramento = enquadrar(cadeia);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(enq.largura);
  canvas.height = Math.ceil(enq.altura);

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return undefined;

  pintarFundo(ctx, enq, paleta);
  pintarLegenda(ctx, enq, opcoes.legenda, paleta);

  const quadros: QuadroCru[] = [
    {
      esquerda: 0,
      topo: 0,
      dados: ctx.getImageData(0, 0, canvas.width, canvas.height),
      atrasoCs: ABERTURA_CS,
    },
  ];

  const marcadasJaPintadas = new Set<string>();
  const levas = emLevas(celulasEmOrdem(cadeia, marcas, enq, paleta));

  levas.forEach((leva, i) => {
    const pintadas = leva.filter((c) => venceOCruzamento(c, marcadasJaPintadas));
    for (const celula of pintadas) {
      pintarCelula(ctx, celula, paleta);
      if (celula.marcada) marcadasJaPintadas.add(celula.chave);
    }
    if (pintadas.length === 0) return;

    // O retângulo vai com uma folga de dois pixels: a borda arredondada e o
    // texto suavizado sangram um pouco para fora da célula.
    const caixa = caixaDe(pintadas);
    const esquerda = Math.max(0, Math.floor(caixa.x) - 2);
    const topo = Math.max(0, Math.floor(caixa.y) - 2);
    const largura = Math.min(canvas.width - esquerda, Math.ceil(caixa.largura) + 4);
    const altura = Math.min(canvas.height - topo, Math.ceil(caixa.altura) + 4);

    quadros.push({
      esquerda,
      topo,
      dados: ctx.getImageData(esquerda, topo, largura, altura),
      atrasoCs: i === levas.length - 1 ? PAUSA_FINAL_CS : PASSO_CS,
    });
  });

  // A paleta sai da imagem pronta, que é onde todas as cores já apareceram.
  const completa = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const paletaCores: readonly Cor[] = paletaMediana(completa.data, CORES);

  const quadrosGif: QuadroGif[] = quadros.map((q) => ({
    esquerda: q.esquerda,
    topo: q.topo,
    largura: q.dados.width,
    altura: q.dados.height,
    indices: indexar(q.dados.data, paletaCores),
    atrasoCs: q.atrasoCs,
  }));

  const bytes = codificarGif({
    largura: canvas.width,
    altura: canvas.height,
    paleta: paletaCores,
    quadros: quadrosGif,
  });

  // `Uint8Array` sobre `ArrayBuffer` é o que o `BlobPart` aceita sem cópia.
  return new Blob([bytes as unknown as BlobPart], { type: 'image/gif' });
}

export function baixarGifDoTabuleiro(opcoes: OpcoesGifTabuleiro, nome: string): boolean {
  const blob = gerarGifDoTabuleiro(opcoes);
  if (blob === undefined) return false;
  baixarArquivo(blob, nome);
  return true;
}
